import React, { useRef } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExport from 'highcharts/modules/export-data';
import HighchartsExporting from 'highcharts/modules/exporting';

import { format, isValid } from 'date-fns';
import styles from './ChartVariation.module.scss';

import ptBR from 'date-fns/locale/pt-BR';
import { Skeleton } from '../../../../../../../components';
import { ChartAnalysis } from '../../../../../../../reducers/OtimizacaoPrecos/optimizationPricesResult';

interface ChartVariationProps {
  title: string;
  titleYAxis: string;
  chart: ChartAnalysis;
  containerRefCharts: any;
  isLoading: boolean;
}

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsExport(Highcharts);

function formatTooltipDate(categories: string[], index: number, formatString: string) {
  const date = categories[index];

  return `<strong>${format(new Date(date), formatString, { locale: ptBR })}</strong>`;
}

export const COLORS = [
  '#E663C9',
  '#0D66D0',
  '#76D680',
  '#FFA164',
  '#825DCB',
  '#5FC8FF',
  '#FF73C3',
  '#5AE47A',
  '#FFD76E',
  '#F25E5E',
  '#609BCE',
  '#B30089',
  '#FFB46E',
];

export const ChartVariationContainer = ({ title, titleYAxis, chart, containerRefCharts, isLoading }: ChartVariationProps) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const width = containerRefCharts?.current?.clientWidth / 2 - 60;

  const options: Highcharts.Options = {
    chart: {
      type: 'line',
      width,
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: '',
    },
    yAxis: {
      labels: {
        enabled: true,
        format: '{value} %',
      },
      title: {
        text: titleYAxis,
        style: {
          fontWeight: 'bold',
          color: '#323232',
          fontSize: '8px',
          textTransform: 'uppercase',
        },
      },
    },
    xAxis: {
      title: {
        text: '',
      },
      categories: chart.dates,
      labels: {
        formatter: function () {
          if (!this.value) return '';
          const isValidDate = isValid(new Date(this.value));
          if (!isValidDate) return '';
          return format(new Date(this.value), 'dd/MM/yyyy', { locale: ptBR });
        },
      },
    },
    colors: COLORS,
    plotOptions: {
      series: {
        label: {
          connectorAllowed: false,
        },
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        if (!this.points) {
          return '';
        }
        const index = this.points[0].point.index;
        const categories = this?.points[0].series.chart.xAxis[0].categories;

        const dateStart = formatTooltipDate(categories, 0, 'dd MMMM ');
        const dateEnd = formatTooltipDate(categories, index, "dd MMMM 'de' yyyy");
        const dateRange = `Data: ${dateStart} - ${dateEnd}<br/>`;
        let seriesInfo = '';
        let totalDiff = '0';

        this.points.forEach(point => {
          const seriesName = point.series.name;
          const value = point.y.toFixed(2).replace('.', ',').replace('-', '');

          const accumulated = (point.series.userOptions as any).data
            ?.filter((_: any, i: number) => i <= index)
            ?.reduce((acc: number, value: number) => acc + value, 0)
            ?.toFixed(2)
            ?.replace('.', ',')
            ?.replace('-', '');

          const valueSignal = Number(point.y) >= 0 ? '+' : '-';
          const accumulatedSignal = Number(accumulated) >= 0 ? '+' : '-';

          seriesInfo += `<span style="color:${point.color};">●</span> ${seriesName}:
                     <strong>${valueSignal}${value} % (acum. ${accumulatedSignal}${accumulated} %)</strong><br/>`;
        });

        if (this.points.length === 2) {
          totalDiff = (this.points[0].y - this.points[1].y).toFixed(2).replace('.', ',').replace('-', '');
        }

        const totalDiffSignal = Number(totalDiff) >= 0 ? '+' : '-';

        const differenceInfo = `Diferença: <strong>${totalDiffSignal}${totalDiff} %</strong>`;

        return `<div style="padding: 8px;">${dateRange}${seriesInfo}${differenceInfo}</div>`;
      },
      shared: true,
    },
    series: chart.series.map((serie, index) => ({ ...serie, color: COLORS[index], type: 'line', data: serie?.data?.map(d => d * 100) })),
  };

  if (isLoading) {
    return (
      <div className={styles.container} ref={containerRef} style={{ height: 470, width: width }}>
        <Skeleton width={'100%'} height={450} />
      </div>
    );
  }

  return (
    <div className={styles.container} ref={containerRef} style={{ height: '100%', width: width }}>
      {chart.status === 'success' && (
        <>
          <p>{title}</p>
          <div ref={containerRef} className={styles.chart}>
            <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} containerProps={{ style: { width: '100%', height: '100%' } }} />
          </div>
        </>
      )}
    </div>
  );
};
